import { Col, Row, Tooltip } from "antd";
import dayjs from "dayjs";
import { get, isEmpty, set } from "lodash";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineCheckCircle,
  MdOutlineHealthAndSafety,
  MdOutlineQrCode,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  GENDER,
  RELATIONSHIP,
  SOCIAL_REGIMES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Children = ({
  formProps,
  values,
  setFieldValue,
  setPercent,
  isFieldRequired,
  errors,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const getAge = (birthdate) => {
    if (!birthdate) return 0;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const children = get(initialValues, "children", []);

  const [teletransmission, setTeletransmission] = useState(
    () => !get(children, "[0].identity.fields.teletransmission", true),
  );

  const handleTeletransmissionChange = useCallback(
    (value) => {
      setTeletransmission(value);
      children.forEach((_, index) => {
        set(
          initialValues,
          `children.${index}.identity.fields.teletransmission`,
          !value,
        );
      });
    },
    [children, initialValues],
  );

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.CHILD,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert
                showIcon
                type="info"
                message={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.INFO}
              />
            </Col>
            {children.map((_, index) => {
              const age = getAge(_.identity?.user?.birthDate);
              const isMajor = age > 18;
              return [
                <Col lg={24} xs={24} key={"child-form-" + index}>
                  <Form
                    type="highlight"
                    title={
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.CHILD
                        .TITLE +
                      " " +
                      (index + 1)
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col lg={24} xs={24}>
                        <Input
                          label={SUBSCRIPTION.FORM.LAST_NAME.LABEL}
                          icon={<MdOutlineBadge size={16} />}
                          placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                          defaultValue={get(
                            initialValues,
                            `children.${index}.identity.user.lastname`,
                          )}
                          errors={
                            errors[`children[${index}].identity.user.lastname`]
                          }
                          touched={
                            !isEmpty(
                              errors[
                                `children[${index}].identity.user.lastname`
                              ],
                            )
                          }
                          onChange={({ target: { value } }) => {
                            set(
                              initialValues,
                              `children.${index}.identity.user.lastname`,
                              value,
                            );
                          }}
                          onBlur={() => setPercent(initialValues)}
                          required={isFieldRequired(
                            `children.${index}.identity.user.lastname`,
                          )}
                        />
                      </Col>
                      <Col lg={24} xs={24}>
                        <Input
                          placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                          label={SUBSCRIPTION.FORM.FIRST_NAME.LABEL}
                          icon={<MdOutlineBadge size={16} />}
                          defaultValue={get(
                            initialValues,
                            `children.${index}.identity.user.firstname`,
                          )}
                          errors={
                            errors[`children[${index}].identity.user.firstname`]
                          }
                          touched={
                            !isEmpty(
                              errors[
                                `children[${index}].identity.user.firstname`
                              ],
                            )
                          }
                          onChange={({ target: { value } }) => {
                            set(
                              initialValues,
                              `children.${index}.identity.user.firstname`,
                              value,
                            );
                          }}
                          onBlur={() => setPercent(initialValues)}
                          required={isFieldRequired(
                            `children.${index}.identity.user.firstname`,
                          )}
                        />
                      </Col>
                      <Col lg={24} xs={24}>
                        <Input
                          type="radio"
                          variant="group"
                          label={SUBSCRIPTION.FORM.CHILD_GENDER.LABEL}
                          values={[
                            {
                              label:
                                SUBSCRIPTION.FORM.CHILD_GENDER.OPTIONS.MALE,
                              value: GENDER.MALE,
                            },
                            {
                              label:
                                SUBSCRIPTION.FORM.CHILD_GENDER.OPTIONS.FEMALE,
                              value: GENDER.FEMALE,
                            },
                          ]}
                          icon={<MdOutlineBadge size={22} />}
                          value={get(
                            initialValues,
                            `children.${index}.identity.user.gender`,
                          )}
                          errors={
                            errors[`children[${index}].identity.user.gender`]
                          }
                          touched={
                            !isEmpty(
                              errors[`children[${index}].identity.user.gender`],
                            )
                          }
                          onChange={(value) => {
                            set(
                              initialValues,
                              `children.${index}.identity.user.gender`,
                              value,
                            );
                          }}
                          onBlur={() => setPercent(initialValues)}
                          required={isFieldRequired(
                            `children.${index}.identity.user.gender`,
                          )}
                        />
                      </Col>
                      <Col lg={24} xs={24}>
                        <Select
                          label={SUBSCRIPTION.FORM.MANDATORY_SCHEME.LABEL}
                          icon={<MdOutlineHealthAndSafety size={16} />}
                          placeholder={
                            SUBSCRIPTION.FORM.MANDATORY_SCHEME.PLACEHOLDER
                          }
                          type="simple"
                          options={Object.entries(SOCIAL_REGIMES).map(
                            ([key, value]) => ({
                              value,
                              label:
                                SUBSCRIPTION.FORM.MANDATORY_SCHEME
                                  .MUTUAL_MOBILITY_OPTIONS[key],
                            }),
                          )}
                          defaultValue={get(
                            initialValues,
                            `children.${index}.identity.socialRegime`,
                          )}
                          errors={
                            errors[`children.${index}.identity.socialRegime`]
                          }
                          touched={
                            !isEmpty(
                              errors[`children.${index}.identity.socialRegime`],
                            )
                          }
                          onChange={(value) => {
                            set(
                              initialValues,
                              `children.${index}.identity.socialRegime`,
                              value,
                            );
                          }}
                          required={isFieldRequired(
                            `children.${index}.identity.socialRegime`,
                          )}
                          disabled
                        />
                      </Col>
                      <Col lg={24} xs={24}>
                        <Input
                          label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                          icon={<MdOutlineCake size={16} />}
                          placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                          type="date"
                          variant="simple"
                          defaultValue={dayjs(
                            get(
                              initialValues,
                              `children.${index}.identity.user.birthDate`,
                            ),
                          )}
                          format={DATE_FORMAT}
                          disabled
                          required={isFieldRequired(
                            `children.${index}.identity.user.birthDate`,
                          )}
                        />
                      </Col>
                      <Col lg={24} xs={24}>
                        <Tooltip
                          title={SUBSCRIPTION.FORM.TELETRANSMISSION.SYNC_NOTICE}
                        >
                          <div>
                            <Input
                              type="switch"
                              label={SUBSCRIPTION.FORM.TELETRANSMISSION.LABEL}
                              icon={<MdOutlineCheckCircle size={16} />}
                              checked={teletransmission}
                              errors={
                                errors[
                                  `children[${index}].identity.fields.teletransmission`
                                ]
                              }
                              touched={
                                !isEmpty(
                                  errors[
                                    `children[${index}].identity.fields.teletransmission`
                                  ],
                                )
                              }
                              onChange={handleTeletransmissionChange}
                            />
                          </div>
                        </Tooltip>
                      </Col>
                      {isMajor && (
                        <>
                          <Col lg={24} xs={24}>
                            <Input
                              type="switch"
                              label={
                                SUBSCRIPTION.FORM.REFUND_OTHER_ACCOUNT.LABEL
                              }
                              icon={<MdOutlineCheckCircle size={16} />}
                              defaultChecked={get(
                                initialValues,
                                `children.${index}.identity.fields.refundOtherAccount`,
                              )}
                              errors={
                                errors[
                                  `children[${index}].identity.fields.refundOtherAccount`
                                ]
                              }
                              touched={
                                !isEmpty(
                                  errors[
                                    `children[${index}].identity.fields.refundOtherAccount`
                                  ],
                                )
                              }
                              onChange={(value) => {
                                set(
                                  initialValues,
                                  `children.${index}.identity.fields.refundOtherAccount`,
                                  value,
                                );
                                setPercent(initialValues);
                              }}
                            />
                          </Col>
                          {get(
                            initialValues,
                            `children.${index}.identity.fields.refundOtherAccount`,
                          ) && (
                            <>
                              <Col lg={24} xs={24}>
                                <Input
                                  label={SUBSCRIPTION.FORM.IBAN.LABEL}
                                  icon={<MdOutlineQrCode size={16} />}
                                  type="custom"
                                  trim={true}
                                  uppercase={true}
                                  placeholder={
                                    SUBSCRIPTION.FORM.IBAN.PLACEHOLDER
                                  }
                                  defaultValue={get(
                                    initialValues,
                                    `children.${index}.identity.fields.payment.IBAN`,
                                  )}
                                  errors={
                                    errors[
                                      `children[${index}].identity.fields.payment.IBAN`
                                    ]
                                  }
                                  touched={
                                    !isEmpty(
                                      errors[
                                        `children[${index}].identity.fields.payment.IBAN`
                                      ],
                                    )
                                  }
                                  onChange={({ target: { value } }) => {
                                    set(
                                      initialValues,
                                      `children.${index}.identity.fields.payment.IBAN`,
                                      value,
                                    );
                                  }}
                                  onBlur={() => setPercent(initialValues)}
                                  required={isFieldRequired(
                                    `children.${index}.identity.fields.payment.IBAN`,
                                  )}
                                />
                              </Col>
                              <Col lg={24} xs={24}>
                                <Input
                                  label={SUBSCRIPTION.FORM.BIC.LABEL}
                                  icon={<MdOutlineQrCode size={16} />}
                                  placeholder={
                                    SUBSCRIPTION.FORM.BIC.PLACEHOLDER
                                  }
                                  defaultValue={get(
                                    initialValues,
                                    `children.${index}.identity.fields.payment.BIC`,
                                  )}
                                  errors={
                                    errors[
                                      `children[${index}].identity.fields.payment.BIC`
                                    ]
                                  }
                                  touched={
                                    !isEmpty(
                                      errors[
                                        `children[${index}].identity.fields.payment.BIC`
                                      ],
                                    )
                                  }
                                  onChange={({ target: { value } }) => {
                                    set(
                                      initialValues,
                                      `children.${index}.identity.fields.payment.BIC`,
                                      value,
                                    );
                                  }}
                                  onBlur={() => setPercent(initialValues)}
                                  required={isFieldRequired(
                                    `children.${index}.identity.fields.payment.BIC`,
                                  )}
                                />
                              </Col>
                            </>
                          )}
                        </>
                      )}
                    </Row>
                  </Form>
                </Col>,
              ];
            })}
          </Row>
        </Form>
      </div>
    )
  );
};

export default Children;
